import React, { useState } from "react";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  InputAdornment,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  MenuItem,
  Select,
  FormControlLabel,
  Radio,
  RadioGroup,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import Tab from "@mui/material/Tab";
import TabPanel from "@mui/lab/TabPanel";
import nl from "date-fns/locale/nl";
import Loading from "../common/Loading";
import AppSnackbar from "../../common/AppSnackbar";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { useReservationAdminAPI } from "../../service/useReservationAdminAPI";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { getDateStringToAvoidTimezone } from "../../common/DateUtil";
import InfoRow from "../common/InfoRow";
import { useReservationAPI } from "../../service/useReservationAPI";
import { addDays, parseISO } from "date-fns";
import { useTenantConfig } from "../../security/tenant-config-context";

const AddReservation = ({ initialData, reservationId, onSave, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const isEditMode = !!reservationId || !!initialData;

  const [inputValue, setInputValue] = useState("");
  const { searchByAnimalName, createReservation, updateReservation } =
    useReservationAdminAPI();
  const { checkReservationAvailability, getAvailableStays } =
    useReservationAPI();

  const [animals, setAnimals] = useState([]);
  const [currentAnimal, setCurrentAnimal] = useState({
    animals: [],
    person: {},
  });
  const [newReservationDto, setNewReservationDto] = useState({
    personId: undefined,
    animalsInReservationIds: [],
    fromDate: undefined,
    toDate: undefined,
    checkInHour: undefined,
    checkOutHour: undefined,
    selectedCombo: undefined,
  });
  const [stayCombos, setStayCombos] = useState([]);
  const [noStaysAvailable, setNoStaysAvailable] = useState(true);
  const [openEditModal, setOpenEditModal] = React.useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity] = useState("error");
  const [activeTab, setActiveTab] = useState("1");
  const [canGoToStep2, setCanGoToStep2] = useState(false);

  React.useEffect(() => {
    if (isEditMode && initialData) {
      setNewReservationDto({
        uuid: initialData.uuid,
        personId: initialData.personId,
        animalsInReservationIds: initialData.animalsInReservationIds || [],
        fromDate: initialData.fromDate,
        toDate: initialData.toDate,
        checkInHour: initialData.checkInHour,
        checkOutHour: initialData.checkOutHour,
        selectedCombo: undefined,
      });
      setCurrentAnimal({
        person: initialData.person || {},
        animals: initialData.animals || [],
      });
      setCanGoToStep2(true);
      setActiveTab("1");
      setOpenEditModal(true);
    }
  }, [isEditMode, initialData]);

  const { cfg } = useTenantConfig();
  const isDogBusiness = cfg?.animalTypes?.includes("DOG");

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    if (!isEditMode) {
      setNewReservationDto({
        personId: undefined,
        animalsInReservationIds: [],
        fromDate: undefined,
        toDate: undefined,
        checkInHour: undefined,
        checkOutHour: undefined,
        selectedCombo: undefined,
      });
      setStayCombos([]);
      setNoStaysAvailable(true);
      setActiveTab("1");
      setCanGoToStep2(false);
    }
    onClose?.();
  };

  const handleAddOption = () => {
    if (!inputValue) return;
    searchByAnimalName(inputValue)
      .then((animals) => setAnimals(animals))
      .catch(() => handleFetchError(t("searchFailure")));
  };

  const handleSaveReservation = () => {
    const action = isEditMode
      ? updateReservation(newReservationDto)
      : createReservation(newReservationDto);

    action
      .then(() => {
        handleCloseEditModal();
        handleSuccessMessage(
          isEditMode
            ? "updatedReservationSuccessful"
            : "createdReservationSuccessful",
        );
        onSave?.();
      })
      .catch(() =>
        handleFetchError(
          t(isEditMode ? "updateReservationFailed" : "createReservationFailed"),
        ),
      );
  };

  function handleFetchError(reason) {
    setSnackbarMessage(reason);
    setSeverity("error");
    setOpenSnackbar(true);
  }

  function handleSuccessMessage(reason) {
    setSnackbarMessage(reason);
    setSeverity("success");
    setOpenSnackbar(true);
  }

  const handleInputChange = (event) => setInputValue(event.target.value);

  const handleSelectChange = (eventOrValue, field) => {
    let value = eventOrValue?.target ? eventOrValue.target.value : eventOrValue;
    if (field === "fromDate" || field === "toDate") {
      value = value ? getDateStringToAvoidTimezone(value) : undefined;
    }
    setNewReservationDto((prev) => ({ ...prev, [field]: value }));
  };

  const toggleAnimalForReservation = (id) => {
    setNewReservationDto((prev) => {
      const exists = prev.animalsInReservationIds.includes(id);
      return {
        ...prev,
        animalsInReservationIds: exists
          ? prev.animalsInReservationIds.filter((x) => x !== id)
          : [...prev.animalsInReservationIds, id],
      };
    });
  };

  function checkAvailability() {
    const { fromDate, toDate, animalsInReservationIds, uuid } = newReservationDto;
    if (!fromDate || !toDate || animalsInReservationIds.length < 1) return;

    checkReservationAvailability(
      fromDate,
      toDate,
      animalsInReservationIds.length,
      uuid,
    )
      .then((result) => {
        if (result) {
          getAvailableStays(
            fromDate,
            toDate,
            animalsInReservationIds.length,
            uuid,
          )
            .then((availableStays) => {
              setStayCombos(availableStays);
              const none = availableStays.length === 0;
              setNoStaysAvailable(none);
              setCanGoToStep2(true);
              setActiveTab("2");
            })
            .catch(() => handleFetchError(t("fetchAvailableStaysFailed")));
        } else {
          setNoStaysAvailable(true);
          setCanGoToStep2(true);
          setActiveTab("2");
        }
      })
      .catch(() => handleFetchError(t("checkAvailabilityFailed")));
  }

    const getLabel = (stayCombo) => {
        return t('selectStayDisplay', {
            price: stayCombo.totalPricePerDay,
            count: stayCombo.totalPets,
            animalTypeLabel: isDogBusiness ?  t('animalTypeLabel.DOG') : t('animalTypeLabel.CAT'),
        });
    }

    const getLineLabel = (line) => {
        return t('selectStayLineDisplay', {
            count: line.petsAssigned,
            animalTypeLabel: isDogBusiness ?  t('animalTypeLabel.DOG') : t('animalTypeLabel.CAT'),
            name: line.stayName
        });
    }

  const minEndDate = newReservationDto.fromDate
    ? addDays(parseISO(newReservationDto.fromDate), 1)
    : undefined;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={nl}>
      <Grid
        container
        justifyContent="center"
        sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 2 } }}
      >
        <Grid item xs={12} sm={12} sx={{ p: 2, m: 2 }}>
          <Grid item xs={12}>
            <Typography variant={isXs ? "h6" : "h5"} sx={{ mb: 1 }}>
              {t("addReservation")}
            </Typography>
          </Grid>

          {/* Search */}
          <Grid item xs={12}>
            <TextField
              fullWidth={isXs}
              required
              value={inputValue}
              onChange={handleInputChange}
              label={t("animalNameLabel")}
              variant="outlined"
              inputProps={{ "data-testid": "search-animal-input" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button onClick={handleAddOption} variant="contained" data-testid="search-animal-button">
                      <SearchIcon />
                    </Button>
                  </InputAdornment>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddOption();
                }
              }}
            />
          </Grid>

          {/* Results: cards on mobile, table on desktop */}
          {isXs ? (
            <Grid item xs={12}>
              <Grid container spacing={1}>
                {animals.map((ci, idx) => (
                  <Grid item xs={12} key={ci?.person?.id || idx}>
                    <Card variant="outlined">
                      <CardContent sx={{ display: "grid", gap: 1 }}>
                        <InfoRow label={t("catName")} value={ci?.name} />
                        <InfoRow
                          label={t("ownerName")}
                          value={`${ci?.person?.forename ?? ""} ${ci?.person?.name ?? ""}`}
                        />
                        <InfoRow
                          label={t("familie")}
                          value={(ci?.animals ?? [])
                            .map((c) => c.name)
                            .join(", ")}
                        />
                      </CardContent>
                      <CardActions sx={{ justifyContent: "flex-end" }}>
                        <Button
                          onClick={() => {
                            setCurrentAnimal(ci);
                            setOpenEditModal(true);
                            setNewReservationDto((prev) => ({
                              ...prev,
                              personId: ci?.person?.id,
                            }));
                          }}
                          data-testid={`add-reservation-button-${ci?.name}`}
                        >
                          {t("addReservation")}
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <TableContainer component={Paper}>
                <Table size="small" aria-label="search results table">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("catName")}</TableCell>
                      <TableCell>{t("ownerName")}</TableCell>
                      <TableCell>{t("familie")}</TableCell>
                      <TableCell>{t("actions")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {animals.map((ci, index) => (
                      <TableRow key={index}>
                        <TableCell>{ci.name}</TableCell>
                        <TableCell>{`${ci.person.forename} ${ci.person.name}`}</TableCell>
                        <TableCell>
                          {(ci.animals || []).map((c) => c.name).join(", ")}
                        </TableCell>
                        <TableCell>
                          <Button
                            onClick={() => {
                              setCurrentAnimal(ci);
                              setOpenEditModal(true);
                              setNewReservationDto((prev) => ({
                                ...prev,
                                personId: ci.person.id,
                              }));
                            }}
                            color="primary"
                            data-testid={`add-reservation-button-${ci.name}`}
                          >
                            {t("addReservation")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}

          {/* Snackbar */}
          <AppSnackbar
            openSnackbar={openSnackbar}
            snackbarMessage={snackbarMessage}
            setOpenSnackbar={setOpenSnackbar}
            severity={severity}
          />

          {/* Modal → Dialog (mobile-friendly) */}
          <Dialog
            open={openEditModal}
            onClose={handleCloseEditModal}
            fullScreen={fullScreen}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>
              <Typography color="secondary" variant={isXs ? "h6" : "h5"}>
                {isEditMode ? t("editReservation") : t("addReservation")}
              </Typography>
              {currentAnimal?.person && (
                <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
                  {`${currentAnimal.person.forename ?? ""} ${currentAnimal.person.name ?? ""}`}
                </Typography>
              )}
            </DialogTitle>
            <DialogContent dividers>
              <TabContext value={activeTab}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <TabList
                    onChange={(_, v) => setActiveTab(v)}
                    aria-label="reservation steps tabs"
                    data-testid="reservation-dialog-tabs"
                  >
                    <Tab label={t("updateDates")} value="1" data-testid="tab-details" />
                    <Tab
                      label={t("selectStay")}
                      value="2"
                      disabled={!canGoToStep2}
                      data-testid="tab-select-stay"
                    />
                  </TabList>
                </Box>

                <TabPanel value="1" sx={{ px: 0 }}>
                  <Box sx={{ display: "grid", gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        {t("selectCats")}
                      </Typography>
                      {(currentAnimal?.animals ?? []).map((animal) => (
                        <Box
                          key={animal.id}
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <Checkbox
                              disabled={isEditMode}
                            checked={newReservationDto.animalsInReservationIds.includes(
                              animal.id,
                            )}
                            onChange={() =>
                              toggleAnimalForReservation(animal.id)
                            }
                            color="primary"
                          />
                          <Typography>{animal.name}</Typography>
                        </Box>
                      ))}
                    </Box>

                    <Box sx={{ display: "grid", gap: 1 }}>
                      <DatePicker
                        label={t("start_date")}
                        value={
                          newReservationDto.fromDate
                            ? (typeof newReservationDto.fromDate === 'string' ? parseISO(newReservationDto.fromDate) : newReservationDto.fromDate)
                            : null
                        }
                        onChange={(v) => handleSelectChange(v, "fromDate")}
                        renderInput={(params) => (
                          <TextField {...params} fullWidth />
                        )}
                      />

                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <Select
                          value={newReservationDto.checkInHour ?? ""}
                          displayEmpty
                          variant={"outlined"}
                          inputProps={{ "aria-label": "check-in" }}
                          sx={{ width: 120 }}
                          renderValue={(value) =>
                            !value ? <em>{t("hour")}</em> : value
                          }
                          onChange={(e) => handleSelectChange(e, "checkInHour")}
                        >
                          {Array.from({ length: 38 }, (_, i) => {
                            const hour = 5 + Math.floor(i / 2);
                            const minute = i % 2 === 0 ? "00" : "30";
                            return `${hour.toString().padStart(2, "0")}:${minute}`;
                          }).map((opt) => (
                            <MenuItem key={`in-${opt}`} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>

                      <DatePicker
                        label={t("end_date")}
                        minDate={minEndDate}
                        value={
                          newReservationDto.toDate
                            ? (typeof newReservationDto.toDate === 'string' ? parseISO(newReservationDto.toDate) : newReservationDto.toDate)
                            : null
                        }
                        onChange={(v) => handleSelectChange(v, "toDate")}
                        renderInput={(params) => (
                          <TextField {...params} fullWidth />
                        )}
                      />

                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <Select
                          value={newReservationDto.checkOutHour ?? ""}
                          displayEmpty
                          inputProps={{ "aria-label": "check-out" }}
                          variant={"outlined"}
                          sx={{ width: 120 }}
                          renderValue={(value) => {
                            if (!value) return <em>{t("hour")}</em>;
                            // the value format is HH:mm:ss — display as HH:mm
                            const [hh, mm] = String(value).split(":");
                            return `${hh}:${mm}`;
                          }}
                          onChange={(e) =>
                            handleSelectChange(e, "checkOutHour")
                          }
                        >
                          {Array.from({ length: 38 }, (_, i) => {
                            const hour = 5 + Math.floor(i / 2);
                            const minute = i % 2 === 0 ? "00" : "30";
                            const seconds = "00";
                            const value = `${hour.toString().padStart(2, "0")}:${minute}:${seconds}`;
                            const label = `${hour.toString().padStart(2, "0")}:${minute}`;
                            return { value, label };
                          }).map((opt) => (
                            <MenuItem
                              key={`out-${opt.value}`}
                              value={opt.value}
                            >
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    </Box>
                  </Box>
                </TabPanel>

                <TabPanel value="2" sx={{ px: 0 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      {t("choose_stay_type")}
                    </Typography>
                    {noStaysAvailable ? (
                      <Alert sx={{ mt: 1 }} severity="warning">
                        {t("noAvailableStays")}
                      </Alert>
                    ) : (
                      <RadioGroup>
                        {stayCombos.map((combo) => (
                          <FormControlLabel
                            key={combo}
                            value={combo}
                            control={<Radio />}
                            label={
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {getLabel(combo)}
                                    </Typography>
                                    {combo.lines.map((line, lineIndex) => (
                                        <Typography key={lineIndex} variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                                            • {getLineLabel(line)}
                                        </Typography>
                                    ))}
                                </div>
                            }
                            checked={newReservationDto.selectedCombo === combo}
                            onChange={() =>
                              setNewReservationDto((prev) => ({
                                ...prev,
                                  selectedCombo: combo,
                              }))
                            }
                          />
                        ))}
                      </RadioGroup>
                    )}
                  </Box>
                </TabPanel>
              </TabContext>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseEditModal} data-testid="cancel-reservation-button">{t("cancel")}</Button>
              {activeTab === "1" && (
                <Button
                  onClick={checkAvailability}
                  disabled={
                    !newReservationDto.fromDate ||
                    !newReservationDto.toDate ||
                    newReservationDto.animalsInReservationIds.length < 1
                  }
                  data-testid="check-availability-button"
                >
                  {t("checkAvailability")}
                </Button>
              )}
              <Button
                onClick={handleSaveReservation}
                disabled={
                  !(
                    newReservationDto.selectedCombo !== undefined &&
                    newReservationDto.animalsInReservationIds.length > 0 &&
                    newReservationDto.fromDate &&
                    newReservationDto.toDate
                  )
                }
                data-testid="save-reservation-button"
              >
                {t("save")}
              </Button>
            </DialogActions>
          </Dialog>

          <AppSnackbar
            openSnackbar={openSnackbar}
            snackbarMessage={snackbarMessage}
            setOpenSnackbar={setOpenSnackbar}
            severity={severity}
          />
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
};

export default withAuthenticationRequired(AddReservation, {
  onRedirecting: () => <Loading />,
  returnTo: "/login",
});
